import { alpha } from "@mui/material/styles";
import { tokens } from "../../assets/theme";

/*
 * The module's colours, set to OEE's own rather than a palette of its own.
 *
 * OEE draws its content as flat colors.primary[400] tiles straight on the colors.primary[500]
 * page — the machine list, the machine page's infoboxes, the settings card, every DataGrid — with
 * colors.primary[700] as the line between things, square corners and no shadow. CMMS used to
 * draw navy cards on the navy page, held apart by a translucent border and a drop shadow, which
 * is why moving between the two felt like changing application. In dark mode `surface` is now the
 * OEE tile and `border` the OEE grid line; light mode already used primary[400] for `surface` and
 * is unchanged.
 */
export const getAssetHierarchyPalette = (mode) => {
  const colors = tokens(mode);
  const isDark = mode === "dark";

  return {
    accent: colors.axionGreen[500],
    actionBg: colors.axionGreen[500],
    actionHover: "#698a40",
    activeBg: alpha(colors.greenAccent[500], isDark ? 0.22 : 0.14),
    activeText: isDark ? colors.greenAccent[100] : colors.greenAccent[300],
    border: isDark ? colors.primary[700] : alpha(colors.primary[500], 0.14),
    buttonBorder: isDark ? alpha(colors.primary[100], 0.24) : alpha(colors.primary[500], 0.22),
    buttonHover: isDark ? alpha(colors.primary[100], 0.08) : alpha(colors.primary[500], 0.06),
    buttonText: isDark ? colors.gray[100] : colors.gray[100],
    dangerBorder: colors.redAccent[500],
    dangerHover: alpha(colors.redAccent[500], isDark ? 0.16 : 0.12),
    dangerText: isDark ? colors.redAccent[200] : colors.redAccent[400],
    disabledBorder: isDark ? alpha(colors.primary[100], 0.14) : alpha(colors.primary[500], 0.12),
    disabledText: isDark ? colors.gray[500] : colors.gray[600],
    inactiveBg: alpha(colors.blueGray[500], isDark ? 0.26 : 0.18),
    inactiveText: isDark ? colors.gray[200] : colors.gray[500],
    mutedText: isDark ? colors.gray[300] : colors.gray[500],
    selected: alpha(colors.axionGreen[500], isDark ? 0.16 : 0.1),
    selectedBorder: alpha(colors.axionGreen[500], isDark ? 0.72 : 0.56),
    selectedHover: alpha(colors.axionGreen[500], isDark ? 0.24 : 0.16),
    // Menus only. They float over the page, so they keep a shadow; nothing that sits in the page
    // has one any more.
    shadow: isDark ? "0 10px 28px rgba(0,0,0,0.22)" : "0 10px 28px rgba(20,27,45,0.08)",
    statBg: isDark ? colors.primary[400] : alpha(colors.primary[500], 0.04),
    subtleText: isDark ? colors.gray[400] : colors.gray[600],
    surface: colors.primary[400],
    surfaceDialogSection: isDark ? alpha(colors.primary[600], 0.94) : alpha(colors.primary[900], 0.34),
    surfaceHover: isDark ? alpha(colors.primary[100], 0.08) : alpha(colors.primary[500], 0.04),
    surfaceMuted: isDark ? colors.primary[400] : alpha(colors.primary[900], 0.08),
    // OEE's tab strip (machineTabs.jsx, activities/index.jsx). tabBg is their '#43495799' already
    // laid over the page navy, so the strip looks the same on a primary[400] drawer as on the
    // page; left translucent it would vanish into the drawer.
    tabBg: isDark ? "#303746" : colors.primary[900],
    tabBorder: isDark ? colors.primary[700] : colors.primary[800],
    tabIndicator: isDark ? "#ffffff" : colors.gray[100],
    tabSelectedBg: colors.primary[400],
    tabSelectedText: isDark ? "#ffffff" : colors.gray[100],
    tabText: isDark ? "rgba(255, 255, 255, 0.7)" : colors.gray[300],
    text: isDark ? colors.gray[100] : colors.gray[100],
    // The empty part of a progress bar. It sits on a tile, so it needs the page navy to show.
    trackBg: isDark ? colors.primary[500] : alpha(colors.primary[500], 0.04),
    typeBg: isDark ? alpha(colors.primary[900], 0.42) : alpha(colors.primary[500], 0.04),
    typeBorder: isDark ? alpha(colors.primary[100], 0.18) : alpha(colors.primary[500], 0.14),
  };
};

/*
 * The DataGrid Columns/Filters panel, which renders in a portal and so cannot be reached from the
 * wrapper Box's sx the way the toolbar row can.
 *
 * Same root cause as the toolbar: the footer's buttons default to color="primary", which is the
 * navy the panel itself sits on. toolsDataGrid has carried its own copy of this since before CMMS
 * existed; the three CMMS grids share this one.
 */
export const getDataGridPanelSx = (mode) => {
  const colors = tokens(mode);

  return {
    "& .MuiDataGrid-panelFooter .MuiButtonBase-root": { color: colors.gray[100] },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: colors.axionGreen[500],
      opacity: 0.3,
    },
    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
      backgroundColor: colors.axionGreen[500],
    },
  };
};

/*
 * A switch, in the house colours.
 *
 * MUI paints a checked switch in primary.main, which is the same hex as the page background — so
 * in a page header or a filter row a switch vanishes at the moment it is turned on, and inside a grid it is
 * merely the wrong colour. The same defect the buttons had, for the same reason.
 *
 * Checked wins on specificity rather than on being second: `.Mui-checked .MuiSwitch-thumb` is two
 * classes against one. That is what lets this be a plain style object instead of taking the
 * checked value as an argument, the way AssetHierarchyToolbar used to.
 */
export const getSwitchSx = (palette) => ({
  "& .MuiSwitch-thumb": { backgroundColor: palette.mutedText },
  "& .MuiSwitch-track": { backgroundColor: palette.border, opacity: 1 },
  "& .Mui-checked .MuiSwitch-thumb": { backgroundColor: palette.accent },
  "& .Mui-checked + .MuiSwitch-track": { backgroundColor: palette.accent, opacity: 0.35 },
  /*
   * The four rules above are what make a read-only switch need these four.
   *
   * MUI greys a disabled switch by leaving the thumb on `currentColor` and dropping the track's
   * opacity — and both of those are painted over above, outright and unconditionally. Without
   * what follows, a disabled switch looks exactly like a live one and simply does not respond to
   * the click, which is the failure this styling would otherwise hand to every caller that ever
   * passes `disabled`.
   *
   * Last on purpose, and the checked pair is written as one compound selector: `.Mui-disabled`
   * alone only ties with `.Mui-checked` on specificity, and a switch that is both has to come out
   * looking disabled rather than looking on.
   */
  "& .Mui-disabled .MuiSwitch-thumb": { backgroundColor: palette.mutedText, opacity: 0.5 },
  "& .Mui-disabled + .MuiSwitch-track": { backgroundColor: palette.border, opacity: 0.3 },
  "& .Mui-checked.Mui-disabled .MuiSwitch-thumb": { backgroundColor: palette.accent, opacity: 0.5 },
  "& .Mui-checked.Mui-disabled + .MuiSwitch-track": { backgroundColor: palette.accent, opacity: 0.2 },
});

/*
 * A tab bar, drawn the way OEE draws its own.
 *
 * The machine page and activities use one tab strip: boxed tabs side by side, each with a dark
 * border, regular weight at 20px, unselected ones darker and dimmed, the selected one in the tile
 * grey with white text and a white bar under it. This is that strip, built from palette tokens
 * instead of their hard-coded hex values, so it also has a light mode.
 *
 * Put it directly above a `surface` tile with no gap between. The selected tab is the same grey as
 * the tile, so the two read as one piece, the way the machine page's tabs sit on their content.
 *
 * Every rule here wins on specificity, with no ties to be broken by insertion order. With the sx
 * root class prepended, `.MuiTab-root.Mui-selected` is three classes against MUI's two, and
 * `.MuiTabs-indicator` is two against one. Pass `disableRipple` on each Tab, as OEE does; sx
 * cannot turn the ripple off.
 *
 * `fitLabels` drops machineTabs' 150px minimum so each tab is as wide as its label. It is for the
 * tab bars inside a details panel or drawer, where five tabs at 150px no longer fit and the last
 * one ends up behind a scroll arrow.
 */
export const getTabsSx = (palette, { fitLabels = false } = {}) => ({
  "& .MuiTab-root": {
    backgroundColor: palette.tabBg,
    border: `1px solid ${palette.tabBorder}`,
    color: palette.tabText,
    fontSize: (theme) => theme.typography.pxToRem(20),
    fontWeight: (theme) => theme.typography.fontWeightRegular,
    minWidth: fitLabels ? 0 : 150,
    textTransform: "none",
  },
  "& .MuiTab-root:hover": { color: palette.tabSelectedText },
  "& .MuiTab-root.Mui-selected": { backgroundColor: palette.tabSelectedBg, color: palette.tabSelectedText },
  "& .MuiTab-root.Mui-focusVisible": { backgroundColor: palette.tabSelectedBg },
  "& .MuiTabs-indicator": { backgroundColor: palette.tabIndicator },
  "& .MuiTabs-scrollButtons": { color: palette.mutedText },
  "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
});

/*
 * A radio, in the house colours.
 *
 * The same root cause as the switch and the tabs, for the same reason: MUI paints a checked
 * radio in primary.main, which in dark mode is colors.primary[500] — the same hex as the page
 * background — so the dot vanishes at the moment it is checked. In light mode
 * primary.main is near-black, visible but not the house green.
 *
 * "Som maskinsidans" is a target, not a template: the machine pages style no radios of their
 * own — they use MUI's default on a background their primary.main does not collide with. Here
 * the checked dot is palette.accent, the house green in both modes.
 *
 * Each rule wins on specificity, not insertion order. Prepend the root class to break the tie
 * MUI's two-class rules would otherwise force: `.MuiRadio-root.Mui-checked` is three classes
 * against two.
 */
export const getRadioSx = (palette) => ({
  color: palette.mutedText,
  "&.MuiRadio-root.Mui-checked": { color: palette.accent },
  "&.MuiRadio-root.Mui-disabled": { color: palette.disabledBorder },
  "&:hover": { backgroundColor: palette.buttonHover },
});

/*
 * A toggle button group, in the house colours.
 *
 * The same family of defect as the switch and the radio, from the other side: MUI's standard
 * toggle marks the selected button with action.selected, a faint overlay that on the page navy
 * barely tells the chosen option from the others, and color="primary" would paint it the page
 * background outright. The selected button takes the selection green the tree already uses for
 * "this one".
 *
 * Set on the group, so one sx reaches every button. Each rule is one class deeper than MUI's own,
 * which is what lets the selected state win without !important.
 */
export const getToggleButtonGroupSx = (palette) => ({
  "& .MuiToggleButton-root": {
    borderColor: palette.buttonBorder,
    color: palette.mutedText,
    fontWeight: 700,
    px: 1.5,
    textTransform: "none",
  },
  "& .MuiToggleButton-root:hover": { backgroundColor: palette.buttonHover, color: palette.text },
  "& .MuiToggleButton-root.Mui-selected": {
    backgroundColor: palette.selected,
    borderColor: palette.selectedBorder,
    color: palette.accent,
  },
  "& .MuiToggleButton-root.Mui-selected:hover": { backgroundColor: palette.selectedHover },
});

/*
 * A select's field, in the house colours.
 *
 * The focused notched outline defaults to `borderColor: primary.main` — the page background's hex
 * in dark mode — so the border vanishes the moment the field is clicked. `sx` on a `<Select>` is
 * forwarded onto the OutlinedInput root, so the root class and the descendant notched-outline
 * element both resolve from here.
 *
 * The disabled treatment mirrors getOutlinedButtonSx — the same two palette keys, for the same
 * reason. The menu itself is a portal and is styled separately by getMenuSx.
 */
export const getSelectSx = (palette) => ({
  color: palette.text,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.border },
  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: palette.buttonBorder },
  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
  "&.MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: palette.disabledBorder },
  "& .MuiSelect-icon": { color: palette.mutedText },
  "& .MuiSelect-icon.Mui-disabled": { color: palette.disabledText },
});

/*
 * A select's dropdown menu, in the house colours.
 *
 * The menu renders in a portal and cannot be reached from the Select's own sx — the same
 * constraint getDataGridPanelSx documents, and the reason this has to travel through
 * MenuProps.PaperProps.sx rather than plain sx.
 *
 * `backgroundImage: "none"` is not cosmetic: without it MUI's dark-mode Paper elevation gradient
 * sits on top of `surface` and the menu comes out a different colour from the panel it drops out
 * of, which is the thing this helper exists to prevent.
 *
 * The selected row is deliberately left looking like every other row — that is what was asked.
 * Acceptable because the closed field already shows the value and MUI still scrolls the selected
 * item into view. Keyboard focus stays visible via the two Mui-focusVisible rules; losing that
 * would be an accessibility regression, not a styling choice.
 */
export const getMenuSx = (palette) => ({
  backgroundColor: palette.surface,
  backgroundImage: "none",
  border: `1px solid ${palette.border}`,
  boxShadow: palette.shadow,
  "& .MuiMenuItem-root": { color: palette.text },
  "& .MuiMenuItem-root:hover": { backgroundColor: palette.buttonHover },
  "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "transparent" },
  "& .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: palette.buttonHover },
  "& .MuiMenuItem-root.Mui-focusVisible": { backgroundColor: palette.buttonHover },
  "& .MuiMenuItem-root.Mui-selected.Mui-focusVisible": { backgroundColor: palette.buttonHover },
});

/*
 * A floating input label, in the house colours.
 *
 * MUI paints a focused label in primary.main — again the page background's hex in dark mode — so
 * the label vanishes when the field is clicked. The label is a sibling of the Select inside the FormControl,
 * so getSelectSx cannot reach it.
 *
 * Named generically rather than getSelectLabelSx because the AxionTextBox/AxionSelect rows this
 * story's successor will straighten out want the same thing.
 */
export const getInputLabelSx = (palette) => ({
  color: palette.mutedText,
  "&.Mui-focused": { color: palette.accent },
});

/*
 * The 11px an AxionTextBox reserves above itself, for the controls beside it that do not.
 *
 * AxionTextBox — and every text-ish control in that family — carries style={{ marginTop: 11 }} on
 * its inner TextField to clear its floating label. AxionSelect makes room a different way, top:
 * 15px on the shrunk InputLabel, and AxionAutocomplete a third way; neither has a margin. Side by
 * side in a row the two fields start 11px apart, and in a stacked dialog the gaps alternate
 * between 16 and 27.
 *
 * The offset is an inline style on the inner element, so a call site cannot cancel it — a caller's
 * sx lands on the outer FormControl and adds to it. That is why this matches the offset rather
 * than removing it, and why the real fix belongs in the shared control. Handed over as a ticket:
 * CMMS Notes/axion-controls-tickets.md.
 *
 * Takes no palette. It is geometry, not colour, the way getSemanticButtonSx is shape only.
 */
export const getAxionFieldAlignSx = () => ({ mt: "11px" });

/*
 * The page's own dark container, the one OEE's main pages sit in: machine overview (home),
 * gantt, production plan, the OEE grid and settings all wrap their content in
 * `<Box m="20px" sx={{ backgroundColor: colors.primary[700] }}>`. Same margin, same colour, and
 * the padding settings uses so nothing touches the edge.
 *
 * The colour is read from the theme rather than the palette so the pages that have no palette of
 * their own (tools, spare parts, settings) can keep calling this with no arguments.
 */
export const getPageShellSx = () => ({
  backgroundColor: (theme) => tokens(theme.palette.mode).primary[700],
  maxWidth: 1800,
  m: "20px",
  p: 2,
});

/*
 * The page header, drawn straight on the page with no card. OEE pages have no boxed header: the
 * machine page puts its tiles directly on the navy. A card would also be one more box around the
 * tiles inside it (statistics, the include-inactive switch), so the header is only its content.
 * No padding of its own: the page container (getPageShellSx) already has it, and the title lines
 * up with the edges of the tiles below it.
 */
export const getAssetHierarchyHeaderSx = (palette) => ({
  color: palette.text,
});

/*
 * Tiles: panels, sections and statistic tiles are one OEE tile, which is flat primary[400] with
 * square corners and no shadow. The primary[700] hairline is invisible on the page navy. It does
 * the work when one tile sits inside another, as a section in a drawer or a statistic in a tab,
 * where it is the only thing that tells the inner tile from its parent.
 */
export const getAssetHierarchyPanelSx = (palette) => ({
  backgroundColor: palette.surface,
  border: `1px solid ${palette.border}`,
  borderRadius: 0,
  color: palette.text,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  overflow: "hidden",
});

export const getSectionBoxSx = (palette) => ({
  backgroundColor: palette.surfaceMuted,
  border: `1px solid ${palette.border}`,
  borderRadius: 0,
  p: 2,
});

export const getDialogSectionBoxSx = (palette) => ({
  backgroundColor: palette.surfaceDialogSection,
  border: `1px solid ${palette.border}`,
  borderRadius: 0.5,
  p: 2,
});

export const getStatisticTileSx = (palette) => ({
  minWidth: 120,
  px: 1.5,
  py: 1.25,
  backgroundColor: palette.statBg,
  border: `1px solid ${palette.border}`,
  borderRadius: 0,
});

/*
 * ---------------------------------------------------------------------------------------------
 * The type scale (FE-15.7)
 * ---------------------------------------------------------------------------------------------
 *
 * The meeting asked for the font to be "uniform with the rest of OEE". There was no font to make
 * uniform: CMMS sets no fontFamily anywhere, and never did — the theme puts Source Sans Pro on
 * typography and on each of h1-h6 (assets/theme.js), and CMMS inherits it like every other page.
 * What differed was scale and layout, so this is a scale, not a font.
 *
 * Two findings decided which way it went.
 *
 * First, there is no OEE scale to converge on. components/Header.jsx looks like the house page
 * header, but it has exactly one consumer app-wide — scenes/planning/index.jsx. Everything else
 * does something different: h5/600 over a 4px rule in settings and activities, bare
 * `h5 gutterBottom` in the calendar family, and no page title at all on machines, tools, users,
 * stops, oeegrid, home and a dozen more. Four idioms and an outlier. Moving five consistent CMMS
 * pages onto a one-page template would not have made anything uniform, so Header.jsx is recorded
 * as the outlier and CMMS's header — accent icon, title, statistic tiles — stays. (The card it
 * used to sit in did not stay; see getAssetHierarchyHeaderSx.)
 *
 * Second, weight 800 was never real. index.css loads Source Sans Pro at wght@400;600;700, so a
 * requested 800 falls back to the loaded 700 face, and because a bold face exists no synthesis
 * happens. Every `fontWeight: 800` in this module rendered as 700 from the day it was written.
 * That is why AssetHierarchyTree's `isSelected ? 800 : 700` never made the selected node look
 * selected. Nothing here goes above 700, and nothing should: 700 is the heaviest weight in the
 * bundle.
 *
 * The compromise on size: the page title moves h4 -> h3 (20px -> 24px) so it reads as a page
 * title rather than as a section heading, without taking Header.jsx's h2/32px and the re-layout
 * that would force on five table-dense pages. It also frees h4, which meant both "page title" and
 * "KPI figure" (SparePartStockDrawer) until now.
 *
 * `variant` cannot be set from sx, so cmmsTypeScale carries that half. Without it "h3" is a
 * literal on five pages and the scale still has five local copies, which is the thing this
 * section exists to prevent. Read the two together: variant from the map, everything else from
 * the helper.
 *
 * The helpers return plain objects, so a call site that needs a conditional colour spreads and
 * overrides — `{ ...getStatValueSx(p), color: isLow ? "warning.main" : p.text }` — rather than
 * this taking an argument per exception.
 *
 * getAssetHierarchyHeaderSx and getStatisticTileSx deliberately carry no typography at all. They
 * are surface and geometry; this is the type that sits on them.
 */
export const cmmsTypeScale = {
  pageTitle: "h3",
  pageSubtitle: "body2",
  panelTitle: "h5",
  sectionLabel: "subtitle2",
  statLabel: "caption",
  statValue: "h6",
};

export const getPageTitleSx = (palette) => ({
  color: palette.text,
  fontWeight: 700,
});

export const getPageSubtitleSx = (palette) => ({
  color: palette.mutedText,
  mt: 0.75,
});

/*
 * The header icon is sized against the title, not against the icons elsewhere on the page — it is
 * the title's counterweight. mt keeps it on the title's optical baseline rather than on its box's
 * top edge, which sits higher than the cap height.
 */
export const getPageHeaderIconSx = (palette) => ({
  color: palette.accent,
  fontSize: 34,
  mt: 0.25,
});

export const getPanelTitleSx = (palette) => ({
  color: palette.text,
  fontWeight: 700,
});

/*
 * The uppercase label above a group — a detail panel's section, a statistic tile's caption. The
 * letterSpacing is not decoration: uppercase at this size sets tight enough to blur into a single
 * shape without it.
 */
export const getSectionLabelSx = (palette) => ({
  color: palette.subtleText,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "uppercase",
});

export const getStatLabelSx = (palette) => ({
  ...getSectionLabelSx(palette),
  display: "block",
});

export const getStatValueSx = (palette) => ({
  color: palette.text,
  fontWeight: 700,
});

/*
 * The geometry every CMMS button shares. Colour is the caller's business; this is the shape.
 *
 * It was written out once per helper until a third kind of button needed it, at which point one
 * definition beat three copies drifting apart.
 */
const cmmsButtonShape = {
  borderRadius: 0.5,
  fontWeight: 700,
  minHeight: 38,
  px: 1.5,
  textTransform: "none",
};

/*
 * For buttons whose colour already carries meaning — color="warning", color="error". Those read
 * clearly against every CMMS surface as MUI paints them, so only the shape is ours to set.
 */
export const getSemanticButtonSx = () => ({ ...cmmsButtonShape });

/*
 * The tones an outlined button comes in.
 *
 * A lookup rather than the pair of ternaries this was, which had already stopped being readable
 * at two intents and would not have survived a third. `default` and `danger` carry exactly the
 * values they always did — every existing caller looks the same as before.
 *
 * `accent` is the house green, for the one action in a row that is the one you probably came to
 * press. Used sparingly on purpose: a row where everything is emphasised has nothing emphasised.
 */
const getOutlinedButtonTones = (palette) => ({
  default: {
    border: palette.buttonBorder,
    text: palette.buttonText,
    hoverBg: palette.buttonHover,
    hoverBorder: palette.text,
  },
  danger: {
    border: palette.dangerBorder,
    text: palette.dangerText,
    hoverBg: palette.dangerHover,
    hoverBorder: palette.dangerText,
  },
  accent: {
    border: palette.selectedBorder,
    text: palette.accent,
    hoverBg: palette.selected,
    hoverBorder: palette.accent,
  },
});

export const getOutlinedButtonSx = (palette, intent = "default") => {
  const tones = getOutlinedButtonTones(palette);
  // An unknown intent falls back rather than rendering a button with no border and no colour.
  const tone = tones[intent] ?? tones.default;

  return {
    ...cmmsButtonShape,
    borderColor: tone.border,
    color: tone.text,
    opacity: 1,
    "& .MuiButton-startIcon": {
      color: "inherit",
    },
    "&:hover": {
      backgroundColor: tone.hoverBg,
      borderColor: tone.hoverBorder,
    },
    "&.Mui-disabled": {
      borderColor: palette.disabledBorder,
      color: palette.disabledText,
      opacity: 1,
    },
  };
};

export const getContainedButtonSx = (palette) => ({
  ...cmmsButtonShape,
  backgroundColor: palette.actionBg,
  boxShadow: "none",
  color: "#ffffff",
  "&:hover": {
    backgroundColor: palette.actionHover,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: palette.disabledBorder,
    color: palette.disabledText,
    opacity: 1,
  },
});

export const getIconButtonSx = (palette) => ({
  color: palette.buttonText,
  borderRadius: 0.5,
  "&:hover": {
    backgroundColor: palette.buttonHover,
  },
  "&.Mui-disabled": {
    color: palette.disabledText,
    opacity: 1,
  },
});

export const getStatusChipSx = (palette, isActive) => ({
  backgroundColor: isActive ? palette.activeBg : palette.inactiveBg,
  border: `1px solid ${isActive ? alpha(palette.activeText, 0.3) : palette.buttonBorder}`,
  color: isActive ? palette.activeText : palette.inactiveText,
  fontSize: 12,
  fontWeight: 700,
  height: 24,
});

export const getTypeChipSx = (palette) => ({
  backgroundColor: palette.typeBg,
  border: `1px solid ${palette.typeBorder}`,
  color: palette.text,
  fontSize: 12,
  fontWeight: 700,
  height: 24,
});
